import type { ForecastTableControlValues } from "../components/tableControls/forecastTableControlValues";
import { resolvePeriodLabel } from "./timePeriodUtils";

/**
 * Fiscal year start (Aug 6, 2000).
 * All chart dates are projected onto this base year so multiple fiscal years can be overlaid on a single axis.
 * */
export const NORMALIZED_AXIS_START = new Date(2000, 7, 6);

/** Axis max for the time axis: one day before the same date in the following year. */
export function getAxisMax(): Date {
  return new Date(
    NORMALIZED_AXIS_START.getFullYear() + 1,
    NORMALIZED_AXIS_START.getMonth(),
    NORMALIZED_AXIS_START.getDate() - 1
  );
}

/**
 * Maps a fiscal period key (W01–W53, M01–M12, Q1–Q4, H1–H2) to its chart Date
 * in the 2000/2001 base fiscal year.
 *
 * W01 → NORMALIZED_AXIS_START; W02 → +7 days; … (counting from fiscal year start)
 * M01 → NORMALIZED_AXIS_START; M02 → +1 month; …
 * Q1  → NORMALIZED_AXIS_START; Q2  → +3 months; …
 * H1  → NORMALIZED_AXIS_START; H2  → +6 months
 */
export function periodKeyToChartDate(key: string): Date {
  if (/^W\d+$/.test(key)) {
    const n = parseInt(key.slice(1), 10);
    return new Date(
      NORMALIZED_AXIS_START.getTime() + (n - 1) * 7 * 24 * 60 * 60 * 1000
    );
  }
  if (/^M\d+$/.test(key)) {
    const n = parseInt(key.slice(1), 10);
    return new Date(
      NORMALIZED_AXIS_START.getFullYear(),
      NORMALIZED_AXIS_START.getMonth() + (n - 1),
      NORMALIZED_AXIS_START.getDate()
    );
  }
  if (/^Q\d$/.test(key)) {
    const n = parseInt(key.slice(1), 10);
    return new Date(
      NORMALIZED_AXIS_START.getFullYear(),
      NORMALIZED_AXIS_START.getMonth() + (n - 1) * 3,
      NORMALIZED_AXIS_START.getDate()
    );
  }
  if (/^H\d$/.test(key)) {
    const n = parseInt(key.slice(1), 10);
    return new Date(
      NORMALIZED_AXIS_START.getFullYear(),
      NORMALIZED_AXIS_START.getMonth() + (n - 1) * 6,
      NORMALIZED_AXIS_START.getDate()
    );
  }
  return new Date(NaN);
}

/**
 * Maps a chart Date (2000/2001 base) back to a fiscal period key
 * for the given granularity. Returns null for out-of-range dates.
 */
export function chartDateToPeriodKey(
  date: Date,
  timePeriod: ForecastTableControlValues["timePeriod"]
): string | null {
  const msPerDay = 24 * 60 * 60 * 1000;
  switch (timePeriod) {
    case "WK": {
      const dayOffset = Math.round(
        (date.getTime() - NORMALIZED_AXIS_START.getTime()) / msPerDay
      );
      if (dayOffset < 0) return null;
      return `W${String(Math.floor(dayOffset / 7) + 1).padStart(2, "0")}`;
    }
    case "MO": {
      const totalMonths =
        (date.getFullYear() - NORMALIZED_AXIS_START.getFullYear()) * 12 +
        date.getMonth() -
        NORMALIZED_AXIS_START.getMonth();
      if (totalMonths < 0 || totalMonths > 11) return null;
      return `M${String(totalMonths + 1).padStart(2, "0")}`;
    }
    case "QTR": {
      const totalMonths =
        (date.getFullYear() - NORMALIZED_AXIS_START.getFullYear()) * 12 +
        date.getMonth() -
        NORMALIZED_AXIS_START.getMonth();
      if (totalMonths < 0 || totalMonths > 11) return null;
      return `Q${Math.floor(totalMonths / 3) + 1}`;
    }
    case "HY": {
      const totalMonths =
        (date.getFullYear() - NORMALIZED_AXIS_START.getFullYear()) * 12 +
        date.getMonth() -
        NORMALIZED_AXIS_START.getMonth();
      if (totalMonths < 0 || totalMonths > 11) return null;
      return `H${Math.floor(totalMonths / 6) + 1}`;
    }
    default:
      return null;
  }
}

/**
 * Time-axis tick label for a chart date: its period key resolved per the
 * week-label mode (see ``resolvePeriodLabel``), empty for out-of-range
 * dates and PTG. Shared by ForecastGraph (x-axis) and EventCalendar
 * (time axis).
 */
export function formatTimeTick(
  value: Date,
  timePeriod: ForecastTableControlValues["timePeriod"],
  weekLabels: ForecastTableControlValues["weekLabels"],
  gregMapping: Record<string, string> | undefined
): string {
  const key = chartDateToPeriodKey(value, timePeriod);
  if (!key) return "";
  return resolvePeriodLabel(key, weekLabels, gregMapping);
}

/**
 * Returns the AG Charts interval for time axis tick spacing.
 * Used by both ForecastGraph (x-axis) and EventCalendar (y-axis, non-WK modes).
 */
export function getAxisInterval(
  timePeriod: ForecastTableControlValues["timePeriod"]
):
  | { step: { unit: "day" | "month"; step: number } }
  | { values: Date[] }
  | object {
  const month = NORMALIZED_AXIS_START.getMonth();
  const date = NORMALIZED_AXIS_START.getDate();
  switch (timePeriod) {
    case "HY":
      return {
        values: [0, 1].map((i) => new Date(2000, month + i * 6, date))
      };
    case "QTR":
      return {
        values: [0, 1, 2, 3].map((i) => new Date(2000, month + i * 3, date))
      };
    case "MO":
      return { step: { unit: "month", step: 1 } };
    case "WK":
      return { step: { unit: "day", step: 7 } };
    default:
      return {};
  }
}
